<script setup lang="ts">
import { computed } from 'vue'

type PhoneSize = 'xs' | 'sm' | 'md' | 'lg' | 'fluid'

const props = withDefaults(
  defineProps<{
    size?: PhoneSize
    hideBottom?: boolean
    fitContent?: boolean
    scale?: number
  }>(),
  {
    size: 'md',
    hideBottom: false,
    fitContent: false,
    scale: 1
  }
)

const BOX: Record<PhoneSize, string> = {
  xs: 'min-w-[280px] p-2',
  sm: 'w-[280px] max-w-full p-2',
  md: 'w-[340px] max-w-full p-2.5',
  lg: 'w-[400px] max-w-full p-3',
  fluid: 'w-full max-w-[340px] p-2.5'
}

const HEIGHT: Record<PhoneSize, string> = {
  xs: 'h-full',
  sm: 'h-[607px]',
  md: 'h-[737px]',
  lg: 'h-[867px]',
  fluid: 'aspect-[393/852]'
}

const FRAME_RADIUS: Record<PhoneSize, { full: string; top: string }> = {
  xs: { full: 'rounded-t-[44px] rounded-b-[24px]', top: 'rounded-t-[44px]' },
  sm: { full: 'rounded-[44px]', top: 'rounded-t-[44px]' },
  md: { full: 'rounded-[52px]', top: 'rounded-t-[52px]' },
  lg: { full: 'rounded-[60px]', top: 'rounded-t-[60px]' },
  fluid: { full: 'rounded-[52px]', top: 'rounded-t-[52px]' }
}

const SCREEN_RADIUS: Record<PhoneSize, { full: string; top: string }> = {
  xs: { full: 'rounded-t-[36px] rounded-b-[16px]', top: 'rounded-t-[36px]' },
  sm: { full: 'rounded-[36px]', top: 'rounded-t-[36px]' },
  md: { full: 'rounded-[42px]', top: 'rounded-t-[42px]' },
  lg: { full: 'rounded-[48px]', top: 'rounded-t-[48px]' },
  fluid: { full: 'rounded-[42px]', top: 'rounded-t-[42px]' }
}

const frameClass = computed(() => [
  BOX[props.size],
  props.fitContent ? undefined : HEIGHT[props.size],
  props.hideBottom ? [FRAME_RADIUS[props.size].top, 'pb-0'] : FRAME_RADIUS[props.size].full
])
const screenClass = computed(() => (props.hideBottom ? SCREEN_RADIUS[props.size].top : SCREEN_RADIUS[props.size].full))
const frameStyle = computed(() => (props.scale === 1 ? undefined : { zoom: props.scale }))
</script>

<template>
  <div
    class="relative mx-auto bg-[#f4f2ed] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] ring-1 ring-stone-300"
    :class="frameClass"
    :style="frameStyle"
  >
    <div class="relative h-full w-full overflow-hidden bg-white" :class="screenClass">
      <slot />
    </div>
  </div>
</template>
